import React, { Fragment, useEffect, useReducer, useRef } from "react";
import { useDebouncedCallback } from "use-debounce";
import { ToastContainer, toast, Slide } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";

import axios from "axios";
import ai from "../../helpers/axios";
import columns from "./airport-columns";
import TableBulkActions from "../table-bulk-actions";
import Search from "../search";

import { Link } from "react-router-dom";

import DataTableServerPagination from "../data-table-server-pagination";
import PageHeaderRowSearch from "../shared/page-header-row-search";

const reducer = (state, action) => {
  switch (action.type) {
    case "search": {
      return {
        ...state,
        query: action.query,
      };
    }
    case "dataBeganDownloading": {
      return {
        ...state,
        loading: action.payload.loadingStatus,
      };
    }
    case "tableSort": {
      return {
        ...state,
        sortBy: action.payload.sortBy,
        sortDirection: action.payload.sortDirection,
      };
    }
    case "rowsDeleted": {
      return {
        ...state,
        clearSelectedRows: !state.clearSelectedRows,
        selectedRows: [],
      };
    }
    case "rowSelection": {
      return {
        ...state,
        selectedRows: action.payload.selectedRows,
        allSelected: action.payload.allSelected,
      };
    }
    case "dataDownloaded": {
      return {
        ...state,
        airports: {
          data: action.payload.data.airports,
        },
        loading: false,
        totalLength: action.payload.data.countTotal,
      };
    }
    case "pageChanged": {
      return {
        ...state,
        currentPage: action.payload.page,
      };
    }
    case "rowsPerPageChanged": {
      return {
        ...state,
        countPerPage: action.payload.currentRowsPerPage,
      };
    }
    default:
      break;
  }
  return state;
};

const AirportContent = () => {
  const initialState = {
    airports: {
      data: [],
    },
    loading: true,
    countPerPage: 25,
    currentPage: 1,
    query: "",
    selectedRows: [],
    allSelected: false,
    tagFilters: [],
    totalLength: null,
    clearSelectedRows: false,
    sortBy: "airport_actual_name",
    sortDirection: "asc",
  };

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);

  console.log(state);

  const abortControllerRef = useRef(null);

  const getData = async (dbQuery, loadSilently, sortBy, sortDirection) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    let loadingStatus = !loadSilently;
    console.log("getDataFunc", sortBy, sortDirection);

    dispatch({ type: "dataBeganDownloading", payload: { loadingStatus } });

    try {
      const token = await getAccessTokenSilently().catch((e) => {
        console.error(e);
        return loginWithRedirect();
      });

      let { data } = await ai.auth(token).get(`/api/airports/list`, {
        signal,
        params: {
          query: dbQuery,
          page: state.currentPage,
          countPerPage: state.countPerPage,
          sortBy,
          sortDirection,
        },
      });

      dispatch({
        type: "dataDownloaded",
        payload: {
          data,
        },
      });
    } catch (error) {
      if (axios.isCancel(error)) return;
      console.error(error);
    }
  };

  useEffect(() => {
    getData("", false, state.sortBy, state.sortDirection);
    document.title = "Airports | evoJets HQ";
  }, [state.currentPage, state.countPerPage]);

  const handlePageChange = (page, totalRows) => {
    console.log(page, totalRows);

    dispatch({
      type: "pageChanged",
      payload: {
        page,
        totalRows,
      },
    });
  };

  const handleRowsPerPageChange = (currentRowsPerPage, currentPage) => {
    dispatch({
      type: "rowsPerPageChanged",
      payload: {
        currentRowsPerPage,
        currentPage,
      },
    });
  };

  const handleSelection = function (rowData) {
    dispatch({
      type: "rowSelection",
      payload: {
        selectedRows: rowData.selectedRows,
        allSelected: rowData.allSelected,
      },
    });
  };

  const handleDelete = async (e) => {
    e.preventDefault();

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let rowIds = state.selectedRows.map((item) => item._id);
    let { data } = await ai.auth(token).delete("/api/airports/deleteMany", {
      data: { ids: rowIds },
    });

    if (data.deletedCount) {
      getData("", true, state.sortBy, state.sortDirection);

      toast.success("Airport Deleted Successfully", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else {
      console.log("No Rows Deleted");
    }

    // state changes happen regardless if rows successfully deleted or not
    dispatch({
      type: "rowsDeleted",
    });
  };

  const debouncedGetData = useDebouncedCallback(getData, 500);

  const handleSearch = (e) => {
    let query = e.target.value.toLowerCase();
    dispatch({ type: "search", query });
    debouncedGetData(query, true, "airport_actual_name", "asc");
  };

  const handleSort = (column, sortDirection, event) => {
    console.log("sorted", column.selector, sortDirection, event);

    dispatch({
      type: "tableSort",
      payload: {
        sortBy: column.selector,
        sortDirection,
      },
    });

    getData(state.query, true, column.selector, sortDirection);

    //keyField is required for server sort
  };

  return (
    <Fragment>
      {/* <ToastContainer transition={Slide} /> */}

      <PageHeaderRowSearch
        h1={`Airports`}
        pretitle="Settings"
        breadcrumbs={[{ link: "/airports", label: "Airports" }]}
        rightColumn={
          <div
            id="quote-actions-row"
            className="header-actions-row has-delete-btn"
          >
            <Search onSearch={handleSearch} query={state.query}></Search>
            <TableBulkActions
              state={state}
              handleDelete={handleDelete}
              hideTags
              deleteBtnAlignment={"align-right"}
            />
            <Link to="/airports/new" className="evo-blue base-button">
              Add Airport
            </Link>
          </div>
        }
      ></PageHeaderRowSearch>

      <DataTableServerPagination
        onSelection={handleSelection}
        columns={columns}
        handlePageChange={handlePageChange}
        handleRowsPerPageChange={handleRowsPerPageChange}
        state={state}
        data={state.airports.data}
        defaultSortField={"airport_actual_name"}
        handleSort={handleSort}
        sortServer={true}
      ></DataTableServerPagination>

      {/* <pre>{JSON.stringify(state, null, 2)}</pre> */}
    </Fragment>
  );
};

export default AirportContent;
